import type { FormProps } from "antd"
import { Button, Form, message, Space, Spin } from "antd"
import Title from "antd/es/typography/Title"
import { t } from "i18next"
import React, { useCallback, useEffect, useState } from "react"
import "react-quill/dist/quill.snow.css"
import { useNavigate, useParams } from "react-router"
import { Paths } from "~/constants"
import { FaqItem } from "~/modules/parent-question/components"
import FeaturedImage from "~/modules/post/pages/FeaturedImage"
import { FieldType, MediaData, PageData } from "~/types/types"
import pageService from "../services/page"
import PageForm from "./PageForm"
import Publish from "./Publish"

const EditPage = () => {
  const [quillValue, setQuillValue] = React.useState<string>("")
  const [page, setPage] = useState<PageData | null>(null)
  const [status, setStatus] = useState("")
  const [publishDate, setPublishDate] = useState<string | null>(null)
  const [language, setLanguages] = useState("")
  const [selectedImage, setSelectedImage] = useState<MediaData | null>(null)
  const [selectedMetaImage, setSelectedMetaImage] = useState<MediaData | null>(null)
  const [selectedFaqs, setSelectedFaqs] = useState<FaqItem[]>([])
  const [faqEnabled, setFaqEnabled] = useState(false)
  const [form] = Form.useForm()
  const { id } = useParams<{ id: string }>()
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate()
  const pageServices = pageService()

  const onFinish: FormProps<FieldType>["onFinish"] = async values => {
    const dataPayload: any = {
      name: values.name,
      slug: values.slug,
      content: values.content || "",
      publishedAt: status === "Draft" ? null : new Date().toISOString(),
      image: selectedImage,
      SEO: {
        metaTitle: values.metaTitle,
        metaDescription: values.metaDescription,
        keywords: values.keywords,
        metaRobots: values.metaRobots,
        metaViewport: values.metaViewport,
        structuredData: typeof values.structuredData === "string" ? JSON.parse(values.structuredData) : {},
      },
      faq_enabled: faqEnabled,
      faqs: faqEnabled ? selectedFaqs.map((faq, index) => ({
        __component: "content.faq",
        question: faq.question,
        answer: faq.answer,
        order: index,
      })) : [],
    }

    const isSlugChanged = values.slug !== page?.attributes?.slug
    if (isSlugChanged) {
      const isSlugUnique = async (slug: string): Promise<boolean> => {
        try {
          const response = await pageServices.checkSlug(slug)
          return response.data.length === 0
        } catch {
          return false
        }
      }

      const isUnique = await isSlugUnique(values.slug || "")

      if (!isUnique) {
        message.error(t("The path already exists. Please choose another path"))
        return
      }
    }

    try {
      await pageServices.updatePage(Number(id), { data: dataPayload })

      form.resetFields()

      navigate(Paths.PAGES.LIST.PATH)
    } catch {
      message.error(t("Unable to update page. Please try again"))
    }
  }

  const handleFetchPage = useCallback(
    async (id: number) => {
      try {
        setLoading(true)
        const response = await pageServices.fetchPageById(id)

        setPage(response)
        if (response.attributes.publishedAt) {
          setStatus("Published")
        } else {
          setStatus("Draft")
        }

        form.setFieldsValue(response.attributes)
        const seo = response.attributes.SEO || {}
        form.setFieldValue("metaTitle", seo.metaTitle || "")
        form.setFieldValue("metaDescription", seo.metaDescription || "")
        form.setFieldValue("keywords", seo.keywords || "")
        form.setFieldValue("metaRobots", seo.metaRobots || "")
        form.setFieldValue("metaViewport", seo.metaViewport || "")
        form.setFieldValue("structuredData", JSON.stringify(seo.structuredData || {}))

        setLanguages(response.attributes?.locale)
        setQuillValue(response.attributes?.content)
        const image = response.attributes.image?.data
        setSelectedImage(image)
        const metaImage = seo.metaImage?.data
          ? {
            id: seo.metaImage.data.id ?? 0,
            ...seo.metaImage.data.attributes,
          }
          : null
        setSelectedMetaImage(metaImage)

        setFaqEnabled(response.attributes.faq_enabled || false)
        const faqsRaw = response.attributes.faqs
        const faqsArray = faqsRaw?.data || faqsRaw || []
        const faqsData = faqsArray.map((faq: any) => ({
          id: faq.__component ? String(Date.now() + Math.random()) : String(faq.id),
          question: faq.question || faq.attributes?.question,
          answer: faq.answer || faq.attributes?.answer,
        }))
        setSelectedFaqs(faqsData)

        setLoading(false)
      } catch {
        message.error(t("Page fetch error"))
      }
    },
    [form],
  )

  const onFinishFailed: FormProps<FieldType>["onFinishFailed"] = () => { }

  const onChangeImage = (image: MediaData | null) => {
    setSelectedImage(image)
  }

  const onChangeMetaImage = (image: MediaData | null) => {
    setSelectedMetaImage(image)
  }

  const onChangeFaqs = (faqs: FaqItem[]) => {
    setSelectedFaqs(faqs)
  }

  const onFaqEnabledChange = (enabled: boolean) => {
    setFaqEnabled(enabled)
  }

  useEffect(() => {
    if (id) {
      handleFetchPage(Number(id))
    }
  }, [id, handleFetchPage])

  return (
    <>
      {loading ? (
        <div className="flex items-center justify-center min-h-[500px]">
          <Spin size="large" />
        </div>
      ) : (
        <div className="p-4">
          <Space>
            <Title level={2}>{t("Edit Page")}</Title>
            <Button
              onClick={() => navigate(Paths.PAGES.CREATE.PATH)}
              className="bg-white  text-blue-700 px-3 py-1 border-[1px] border-blue-700 rounded-[3px] hover:bg-blue-800 hover:text-white duration-300"
            >
              {t("Add New Page")}
            </Button>
          </Space>
          <div className="flex justify-between">
            <PageForm
              edit
              form={form}
              onFinish={onFinish}
              onFinishFailed={onFinishFailed}
              quillValue={quillValue}
              // @ts-ignore
              page={page}
              setQuillValue={setQuillValue}
              onChangeMetaImage={onChangeMetaImage}
              selectedFaqs={selectedFaqs}
              onChangeFaqs={onChangeFaqs}
              faqEnabled={faqEnabled}
              onFaqEnabledChange={onFaqEnabledChange}
            />
            <div className=" w-[22%] pt-2">
              <Publish
                onSubmit={form.submit}
                isCreateEdit={false}
                setStatus={setStatus}
                status={status}
                setPublishDate={setPublishDate}
                language={language}
                setLanguages={setLanguages}
              />
              <FeaturedImage selectedMedia={selectedImage} setSelectedMedia={onChangeImage} />
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default EditPage
